import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { solicitudesAPI, pagosAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Clock, CreditCard, Wrench, Calendar, AlertTriangle } from 'lucide-react';
import { formatApiError } from '../../utils/apiError';

const PagarCapacidadesPage = () => {
  const { solicitudId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSolicitud();
  }, [solicitudId]);

  const loadSolicitud = async () => {
    try {
      setLoading(true);
      const response = await solicitudesAPI.getCapacidadById(solicitudId);
      setSolicitud(response.data);
      
      // Verificar si la solicitud está pendiente de pago
      if (response.data.estado !== 'aprobada_pendiente_pago') {
        if (response.data.estado === 'aprobada') {
          setError('Esta solicitud ya fue pagada y está activa.');
        } else if (response.data.estado === 'pendiente') {
          setError('Esta solicitud aún no ha sido aprobada por el administrador.');
        } else {
          setError('Esta solicitud no está disponible para pago.');
        }
      }
    } catch (error) {
      console.error('Error loading solicitud:', error);
      setError('No se pudo cargar la información de la solicitud.');
    } finally {
      setLoading(false);
    }
  };

  const handlePagar = async () => {
    if (!user?.taller_id) {
      toast.error('No se encontró información del taller');
      return;
    }

    setProcessingPayment(true);
    try {
      const response = await pagosAPI.crearPreferenciaCapacidades({
        solicitud_id: solicitudId,
        taller_id: user.taller_id,
        email: user.email
      });

      if (response.data?.init_point) {
        // Redirigir a MercadoPago
        window.location.href = response.data.init_point;
      } else {
        toast.error('No se pudo generar el link de pago');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(formatApiError(error, 'Error al procesar el pago'));
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 py-12">
        <div className="max-w-lg mx-auto px-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-amber-800 mb-2">Atención</h2>
              <p className="text-amber-700 mb-6">{error}</p>
              <Button onClick={() => navigate('/panel')} variant="outline">
                Volver al Panel
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader className="bg-teal-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-3">
              <Wrench className="h-6 w-6" />
              Completar Pago - Capacidades Temporales
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Info de la solicitud */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-teal-500">
                  {solicitud?.fabricante_nombre || 'Fabricante'}
                </Badge>
                <Badge variant="outline" className="border-amber-500 text-amber-600">
                  <Clock className="h-3 w-3 mr-1" />
                  Pendiente de Pago
                </Badge>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Modelos solicitados:</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {solicitud?.modelos_nombres?.map((modelo, idx) => (
                    <Badge key={idx} variant="outline" className="text-sm">
                      {modelo}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                Duración: <span className="font-medium">30 días</span> desde el momento del pago
              </div>
            </div>

            {/* Resumen de pago */}
            <div className="border-t border-b py-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Acceso temporal (30 días)</span>
                <span className="font-medium">USD $120.00</span>
              </div>
              <div className="flex justify-between items-center text-lg font-bold">
                <span>Total a pagar</span>
                <span className="text-teal-600">USD $120.00</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                * El monto se convertirá a pesos argentinos al momento del pago según cotización vigente
              </p>
            </div>

            {/* Beneficios */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-800 mb-2">Al completar el pago obtendrás:</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Acceso inmediato a la documentación técnica
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Visualización de manuales por 30 días
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Impresión controlada según tu plan
                </li>
              </ul>
            </div>

            {/* Botón de pago */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate('/panel')}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={handlePagar}
                disabled={processingPayment}
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pagar con MercadoPago
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-gray-500">
              Serás redirigido a MercadoPago para completar el pago de forma segura
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PagarCapacidadesPage;
